import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FocoItem } from 'src/app/api/models/foco';

export interface FocoPdfOptions {
  title: string;
  monthYear: string;
  codigoAnf: string;
  data: FocoItem[];
  displayedColumns: string[];
  orientation?: 'portrait' | 'landscape';
}

export interface FocoStatsPdfOptions {
  monthYear: string;
  codigoAnf: string;
  totalProducts: number;
  totalVendas: number;
  totalFocoInt: number;
  totalProgress: number;
  numProducts100: number;
  chartCanvas: HTMLCanvasElement;
}

/** Map column keys to PDF header labels */
const COLUMN_LABELS: Record<string, string> = {
  cod_produto: 'Código',
  nome: 'Produto',
  tipo: 'Tipo',
  focoInt: 'Objetivo',
  vendas: 'Vendas',
  stock: 'Stock',
  emfalta: 'Em Falta',
  progress: 'Progresso',
  atualizado: 'Última atualização',
};

/** Columns that are UI-only and should never appear in the PDF */
const UI_ONLY_COLUMNS = new Set(['info', 'edit', 'delete', 'editar', 'estado']);

@Injectable({
  providedIn: 'root',
})
export class FocoPdfService {
  private readonly MARGIN = 12;
  private logoDataUrl: string | null = null;

  async generateFocoPdf(opts: FocoPdfOptions): Promise<void> {
    const orientation = opts.orientation ?? 'portrait';
    const pdf = new jsPDF(orientation, 'mm', 'a4');

    // Filter out UI-only columns
    const pdfColumns = opts.displayedColumns.filter(
      (col) => !UI_ONLY_COLUMNS.has(col)
    );

    // Draw header (with logo)
    let y = await this.drawHeader(pdf, opts);

    // Build table
    const head = [pdfColumns.map((col) => COLUMN_LABELS[col] ?? col)];
    const body = opts.data.map((item) =>
      pdfColumns.map((col) => this.getCellValue(item, col))
    );

    autoTable(pdf, {
      startY: y,
      head,
      body,
      margin: { left: this.MARGIN, right: this.MARGIN },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [242, 242, 242],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'left',
      },
      columnStyles: this.buildColumnStyles(pdfColumns),
      didDrawPage: () => {
        // Page number footer on every page
        const pageCount = pdf.getNumberOfPages();
        const currentPage = pdf.getCurrentPageInfo().pageNumber;
        if (pageCount > 1) {
          pdf.setFontSize(8);
          pdf.setTextColor(150);
          const pageWidth = pdf.internal.pageSize.getWidth();
          pdf.text(
            `${currentPage} / ${pageCount}`,
            pageWidth - this.MARGIN,
            pdf.internal.pageSize.getHeight() - 8,
            { align: 'right' }
          );
        }
      },
    });

    const filename = `Foco_${opts.monthYear.replace(/ /g, '_')}.pdf`;
    pdf.save(filename);
  }

  async generateStatsPdf(opts: FocoStatsPdfOptions): Promise<void> {
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Draw header (reuse same logo + title layout)
    let y = await this.drawHeader(pdf, {
      title: 'Estatísticas Foco',
      monthYear: opts.monthYear,
      codigoAnf: opts.codigoAnf,
      data: [],
      displayedColumns: [],
    });

    // Stats summary
    const stats = [
      { label: 'Produtos em foco', value: String(opts.totalProducts) },
      { label: 'Vendidos', value: `${opts.totalVendas} / ${opts.totalFocoInt} (${opts.totalProgress.toFixed(2)}%)` },
      { label: 'Produtos a 100%', value: `${opts.numProducts100} / ${opts.totalProducts}` },
    ];

    const cardWidth = (pageWidth - 2 * this.MARGIN - 16) / 3; // 3 cards with 8mm gaps
    const cardHeight = 14;

    stats.forEach((stat, i) => {
      const x = this.MARGIN + i * (cardWidth + 8);

      // Card background
      pdf.setFillColor(225, 235, 247); // #E1EBF7
      pdf.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');

      // Left accent
      pdf.setFillColor(98, 159, 254); // #629FFE
      pdf.rect(x, y, 1.5, cardHeight, 'F');

      // Value
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(58, 90, 140); // #3a5a8c
      pdf.text(stat.value, x + cardWidth / 2, y + 5.5, { align: 'center' });

      // Label
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(stat.label, x + cardWidth / 2, y + 10.5, { align: 'center' });
    });

    y += cardHeight + 8;

    // Chart title
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(58, 90, 140);
    pdf.text('Vendas por Produto', this.MARGIN, y + 4);
    y += 8;

    // Chart image from canvas
    const chartImg = opts.chartCanvas.toDataURL('image/png');
    const availableWidth = pageWidth - 2 * this.MARGIN;
    const availableHeight = pageHeight - y - 10;
    const aspectRatio = opts.chartCanvas.width / opts.chartCanvas.height;

    let imgWidth = availableWidth;
    let imgHeight = imgWidth / aspectRatio;

    if (imgHeight > availableHeight) {
      imgHeight = availableHeight;
      imgWidth = imgHeight * aspectRatio;
    }

    pdf.addImage(chartImg, 'PNG', this.MARGIN, y, imgWidth, imgHeight);

    const filename = `Estatisticas_Foco_${opts.monthYear.replace(/ /g, '_')}.pdf`;
    pdf.save(filename);
  }

  private async loadLogo(): Promise<string | null> {
    if (this.logoDataUrl) return this.logoDataUrl;
    try {
      const response = await fetch('assets/logo.png');
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoDataUrl = reader.result as string;
          resolve(this.logoDataUrl);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  private capitalize(str: string): string {
    return str
      .split(' ')
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  private async drawHeader(pdf: jsPDF, opts: FocoPdfOptions): Promise<number> {
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = this.MARGIN;
    const logoWidth = 30;
    const logoHeight = 15;
    const rightX = pageWidth - this.MARGIN;

    // Logo (top left)
    const logo = await this.loadLogo();
    if (logo) {
      pdf.addImage(logo, 'PNG', this.MARGIN, y, logoWidth, logoHeight);
    }

    // Title (right-aligned, vertically centered within logo height)
    const titleText = `${opts.title} - ${this.capitalize(opts.monthYear)}`;
    pdf.setFontSize(14);
    pdf.setTextColor(0);
    pdf.text(titleText, rightX, y + 6, { align: 'right' });

    // Código ANF (right-aligned, below title)
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text(`Código ANF: ${opts.codigoAnf}`, rightX, y + 12, { align: 'right' });

    y += logoHeight + 4;

    // Divider line
    pdf.setDrawColor(200);
    pdf.setLineWidth(0.3);
    pdf.line(this.MARGIN, y, pageWidth - this.MARGIN, y);
    y += 6;

    return y;
  }

  private getCellValue(item: FocoItem, col: string): string {
    switch (col) {
      case 'cod_produto':
        return item.produto.cod_produto;
      case 'nome':
        return item.produto.nome.toUpperCase();
      case 'tipo':
        return item.tipo ?? '';
      case 'focoInt':
        return String(item.focoInt ?? 0);
      case 'vendas':
        return String(item.vendas ?? 0);
      case 'stock':
        return String(item.stock ?? 0);
      case 'emfalta': {
        const falta = (item.focoInt ?? 0) - (item.vendas ?? 0);
        return String(falta > 0 ? falta : 0);
      }
      case 'progress': {
        const prog = item.progress ?? 0;
        return `${Number(prog).toFixed(2)}%`;
      }
      case 'atualizado': {
        if (!item.data_Modificado) return '';
        const d = new Date(item.data_Modificado);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      }
      default:
        return '';
    }
  }

  private buildColumnStyles(
    columns: string[]
  ): Record<number, { halign?: 'left' | 'center' | 'right'; cellWidth?: number | 'auto' }> {
    const styles: Record<number, { halign?: 'left' | 'center' | 'right'; cellWidth?: number | 'auto' }> = {};
    columns.forEach((col, i) => {
      switch (col) {
        case 'cod_produto':
          styles[i] = { halign: 'left', cellWidth: 22 };
          break;
        case 'nome':
          styles[i] = { halign: 'left', cellWidth: 'auto' };
          break;
        case 'tipo':
          styles[i] = { halign: 'center', cellWidth: 20 };
          break;
        case 'focoInt':
        case 'vendas':
        case 'stock':
        case 'emfalta':
          styles[i] = { halign: 'center', cellWidth: 18 };
          break;
        case 'progress':
          styles[i] = { halign: 'center', cellWidth: 22 };
          break;
        case 'atualizado':
          styles[i] = { halign: 'center', cellWidth: 28 };
          break;
      }
    });
    return styles;
  }
}
